// The agent cards' view model (SCR-576): per agent, the models it is offered (the effective list),
// its default among them, and the "Narrow for this agent" choices bounded by the global list. The
// effective list is the server's `effectiveModels` (API-573) wherever the draft has not changed
// what decides it; `effectiveLists` is the one seam that picks the server's list or the rule's.
// Also the mapping of the API's model refusals (REQ-210) to the place on the screen that caused
// them. Pure and DOM-free (tests/unit/models/agents-model.test.ts).
import type { ApiError } from "../../api/errors";
import type { components } from "../../api/schema";
import { getPath, type Json } from "../../settings/inherit";
import { providerLabel } from "../../settings/pickers";
import { HARNESSES, policyOf, providersCover, type Harness } from "./model";
import { ALLOWED_MODELS_PATH, catalogProviders, globalAllows, globalOf, nameOf, offeredModels, type CatalogModel } from "./provider-model";

export type EffectiveModels = components["schemas"]["EffectiveModels"];
export type EffectiveLists = Record<Harness["key"], CatalogModel[]>;

/** The server's entry with a person's name: its `displayName` is the family id unless the agent renames it, and an id is never a label. */
function fromServer(entry: components["schemas"]["EffectiveModel"], catalog: readonly CatalogModel[]): CatalogModel {
  const displayName = entry.displayName === entry.model ? nameOf(catalog, entry.model) : entry.displayName;
  return { model: entry.model, displayName, provider: entry.provider, invokable: entry.invokable };
}

function sameJson(a: Json | undefined, b: Json | undefined): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Each agent's effective list. The server's (`effectiveModels`, computed over the installation's
 * inventory, or every installation's at the org default) while the agent's policy and the global
 * list are as saved; the rule over the catalog when the draft changed either, or when the API sent
 * none. Both are ADR-103's rule, so a saved list never jumps.
 */
export function effectiveLists(server: EffectiveModels | undefined, saved: Json | undefined, draft: Json | undefined, catalog: readonly CatalogModel[]): EffectiveLists {
  const globalSame = sameJson(getPath(saved, ALLOWED_MODELS_PATH), getPath(draft, ALLOWED_MODELS_PATH));
  const entries = HARNESSES.map((harness) => {
    const unchanged = globalSame && sameJson(getPath(saved, `models.${harness.key}`), getPath(draft, `models.${harness.key}`));
    const list = server && unchanged ? server[harness.wire].map((m) => fromServer(m, catalog)) : offeredModels(draft, harness, catalog);
    return [harness.key, list] as const;
  });
  return Object.fromEntries(entries) as EffectiveLists;
}

// ------------------------------------------------------------------------------------- the cards

export interface AgentCardModel {
  harness: Harness;
  label: string;
  blurb: string;
  /** The models the agent is offered: the default select's options, exactly. */
  effective: CatalogModel[];
  /** The default, when it is on the list. */
  default: CatalogModel | null;
  /** A saved default outside the list (a document from before REQ-210), named for a person; null otherwise. */
  unavailableDefault: string | null;
  /** No installation has reported its models yet: the list is unknown, not empty, and the saved default is shown as it is. */
  pending: CatalogModel | null;
}

export function agentCards(effective: Json | undefined, lists: EffectiveLists, catalog: readonly CatalogModel[]): AgentCardModel[] {
  const reported = catalog.length > 0;
  return HARNESSES.map((harness) => {
    const list = lists[harness.key];
    const current = policyOf(effective, harness).default;
    const chosen = list.find((m) => m.model === current) ?? null;
    const named = current === "" ? null : { model: current, displayName: nameOf(catalog, current), provider: "", invokable: false };
    return {
      harness,
      label: harness.label,
      blurb: harness.blurb,
      effective: list,
      default: chosen,
      unavailableDefault: reported && chosen === null && named ? named.displayName : null,
      pending: reported ? null : named,
    };
  });
}

// ----------------------------------------------------------------------- narrow for this agent

export interface NarrowProvider {
  provider: string;
  label: string;
  checked: boolean;
  /** The global list offers something of this provider; an option it does not is greyed. */
  allowedGlobally: boolean;
}

export interface NarrowModel {
  model: string;
  displayName: string;
  provider: string;
  checked: boolean;
  allowedGlobally: boolean;
}

export interface NarrowChoices {
  providers: NarrowProvider[];
  /** The models an allow list can name: those of the agent's providers. */
  models: NarrowModel[];
}

/** What "Narrow for this agent" offers: the providers and models, each greyed when the global list refuses it. */
export function narrowChoices(effective: Json | undefined, harness: Harness, catalog: readonly CatalogModel[]): NarrowChoices {
  const policy = policyOf(effective, harness);
  const global = globalOf(effective);
  const providers = catalogProviders(catalog).map((provider) => ({
    provider,
    label: providerLabel(provider),
    checked: providersCover(policy.providers, provider),
    allowedGlobally: catalog.some((m) => m.provider === provider && globalAllows(global, provider, m.model)),
  }));
  const models = catalog
    .filter((m) => providersCover(policy.providers, m.provider))
    .map((m) => ({ model: m.model, displayName: m.displayName, provider: m.provider, checked: policy.allowed.includes(m.model), allowedGlobally: globalAllows(global, m.provider, m.model) }));
  return { providers, models };
}

// ------------------------------------------------------------------------------- the refusals

export const MODEL_RULE_CODES = ["default_not_allowed", "model_outside_global", "default_would_be_disabled"] as const;

export interface AgentErrors {
  /** Shown under the card's Default model select (`default_not_allowed`). */
  default?: string;
  /** Shown on the narrowing picker, which opens (`model_outside_global`). */
  narrow?: string;
}

const POINTER = /^\/models\/([a-z_]+)\/(default|allowed)(?:\/\d+)?$/;

function harnessOfPointer(field: string | undefined): { key: Harness["key"]; part: "default" | "allowed" } | null {
  const match = POINTER.exec(field ?? "");
  const harness = HARNESSES.find((h) => h.key === match?.[1]);
  return harness && match ? { key: harness.key, part: match[2] as "default" | "allowed" } : null;
}

/**
 * Where each model refusal belongs (REQ-210): `default_not_allowed` on the agent's Default select,
 * `model_outside_global` on its narrowing picker, `default_would_be_disabled` per agent (the modal's
 * footer reads these). Keyed by agent; the details' JSON pointers name the agent, the message is
 * the server's.
 */
export function agentErrorsFrom(error: ApiError): Partial<Record<Harness["key"], AgentErrors>> {
  const out: Partial<Record<Harness["key"], AgentErrors>> = {};
  if (!(MODEL_RULE_CODES as readonly string[]).includes(error.code)) return out;
  for (const detail of error.details) {
    const at = harnessOfPointer(detail.field);
    if (!at) continue;
    const slot = (out[at.key] ??= {});
    const text = detail.message ?? error.message;
    if (at.part === "default") slot.default ??= text;
    else slot.narrow ??= text;
  }
  return out;
}

/** Whether a refusal is one this screen places by agent rather than in the page's banner. */
export function isPlacedRefusal(error: ApiError): boolean {
  return Object.keys(agentErrorsFrom(error)).length > 0;
}
